/// <reference path="../types/lightspeed-kseries.d.ts" />

import type {
  AccountLine,
  PaymentLine,
  PosAccountSnapshot,
} from "../shared/types";

export interface LightspeedClient {
  getCurrentAccount(): Promise<PosAccountSnapshot>;
  load<T = unknown>(key: string): Promise<T | undefined>;
  store<T = unknown>(key: string, value: T): Promise<void>;
  /** K-Series adds asynchronously; callers must poll getCurrentAccount afterwards. */
  addItem(itemId: string, quantity?: number): Promise<void>;
  addPayment(payment: {
    paymentMethodId: string;
    amountMinor: number;
  }): Promise<PosAccountSnapshot>;
  addExternalReference(reference: string, prefix?: string): Promise<void>;
  /** A successful callback may not include account data after the account closes. */
  payAccount(payment: {
    paymentMethodId: string;
  }): Promise<PosAccountSnapshot | undefined>;
  commitAccount(force?: boolean): Promise<PosAccountSnapshot>;
  navigate(destination?: "checkout"): Promise<void>;
  close(): Promise<void>;
}

type Bridge = Record<string, unknown>;

function asMinor(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.round(value)
    : 0;
}

function asIdentifier(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.length > 0) return value;
    if (typeof value === "number" && Number.isFinite(value))
      return String(value);
  }
  return "";
}

function asOptionalIdentifier(...values: unknown[]): string | undefined {
  const identifier = asIdentifier(...values);
  return identifier || undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function decodeAccount(value: unknown): PosAccount {
  let decoded = value;
  if (typeof decoded === "string") {
    try {
      decoded = JSON.parse(decoded) as unknown;
    } catch {
      throw new Error("Lightspeed returned an invalid current account payload");
    }
  }
  if (isRecord(decoded) && isRecord(decoded.account)) {
    decoded = decoded.account;
  }
  if (!isRecord(decoded)) {
    throw new Error("Lightspeed returned an invalid current account payload");
  }
  return decoded as PosAccount;
}

export function normalizeAccount(value: unknown): PosAccountSnapshot {
  const raw = decodeAccount(value);
  const lines = (raw.transactionLines ?? []).map(
    (line): AccountLine => ({
      id: asOptionalIdentifier(line.id, line.identifier),
      itemId: asIdentifier(line.itemId, line.itemIdentifier, line.itemSku),
      quantity: Number(line.quantity ?? 1),
      totalAmountMinor: asMinor(line.amount),
    }),
  );
  const payments = (raw.paymentLines ?? []).map(
    (line): PaymentLine => ({
      id: asOptionalIdentifier(line.id, line.identifier),
      amountMinor: asMinor(line.amount),
    }),
  );
  const accountId = asIdentifier(
    raw.id,
    raw.uuid,
    raw.accountId,
    raw.accountObjectId,
    raw.identifier,
    raw.accountNumber,
  );
  if (!accountId.trim()) {
    throw new Error(
      `Lightspeed current account contains no supported identifier (fields: ${Object.keys(raw).sort().join(", ") || "none"})`,
    );
  }
  return {
    accountId,
    totalAmountMinor: asMinor(raw.totalAmount),
    paidAmountMinor: asMinor(raw.paidAmount),
    currency: raw.currencyCode ?? raw.currency ?? "EUR",
    lines,
    payments,
  };
}

function requireFunction(
  bridge: Bridge,
  name: string,
): (...args: unknown[]) => void {
  const fn = bridge[name];
  if (typeof fn !== "function")
    throw new Error(
      `Lightspeed K-Series bridge function ${name} is unavailable`,
    );
  return fn as (...args: unknown[]) => void;
}

function responsePromise<T>(
  bridge: Bridge,
  name: string,
  args: unknown[] = [],
): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve, reject) => {
    try {
      requireFunction(bridge, name)(...args, (response: PosResponse<T>) => {
        if (response?.error) {
          const message =
            typeof response.error === "object" &&
            response.error !== null &&
            "message" in response.error
              ? String(response.error.message)
              : String(response.error);
          reject(
            response.error instanceof Error
              ? response.error
              : new Error(message),
          );
        } else resolve(response?.data);
      });
    } catch (error) {
      reject(error);
    }
  });
}

function voidCall(
  bridge: Bridge,
  name: string,
  args: unknown[] = [],
): Promise<void> {
  try {
    requireFunction(bridge, name)(...args);
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
}

export function createLightspeedClient(
  bridge: Bridge = globalThis as Bridge,
): LightspeedClient {
  const getCurrentAccount = async () => {
    const data = await responsePromise<unknown>(
      bridge,
      "pos_getCurrentAccount",
    );
    if (!data) throw new Error("Lightspeed returned no current account");
    return normalizeAccount(data);
  };
  return {
    getCurrentAccount,
    load: async <T>(key: string): Promise<T | undefined> => {
      const stored = await responsePromise<unknown>(bridge, "pos_loadData", [
        key,
      ]);
      if (typeof stored !== "string") return stored as T | undefined;
      try {
        return JSON.parse(stored) as T;
      } catch {
        return stored as T;
      }
    },
    store: async (key, value) =>
      voidCall(bridge, "pos_storeData", [key, JSON.stringify(value)]),
    addItem: async (itemId, quantity = 1) => {
      if (!Number.isInteger(quantity) || quantity <= 0)
        throw new Error("Item quantity must be a positive integer");
      for (let i = 0; i < quantity; i += 1)
        await voidCall(bridge, "pos_addItemToCurrentAccount", [itemId]);
    },
    addPayment: async ({ amountMinor, paymentMethodId }) => {
      const data = await responsePromise<unknown>(bridge, "pos_addPayment", [
        amountMinor,
        paymentMethodId,
      ]);
      return data ? normalizeAccount(data) : getCurrentAccount();
    },
    addExternalReference: async (reference, prefix = "ADYEN") =>
      voidCall(bridge, "pos_addExternalReference", [reference, prefix]),
    payAccount: async ({ paymentMethodId }) => {
      const data = await responsePromise<unknown>(bridge, "pos_payAccount", [
        paymentMethodId,
      ]);
      // K-Series defines the callback as the success/error boundary, but its
      // data is untyped. Some POS versions close the account successfully and
      // return an empty object, which is an acknowledgement rather than an
      // account snapshot.
      if (data === undefined || data === null) return undefined;
      if (isRecord(data) && Object.keys(data).length === 0) return undefined;
      return normalizeAccount(data);
    },
    commitAccount: async (force = false) => {
      await voidCall(
        bridge,
        force ? "pos_forceCommitCurrentAccount" : "pos_commitCurrentAccount",
      );
      return getCurrentAccount();
    },
    navigate: async () => voidCall(bridge, "pos_navigateToCheckoutView"),
    close: async () => voidCall(bridge, "pos_close"),
  };
}

export interface MockLightspeedOptions {
  account?: Partial<PosAccountSnapshot>;
  itemPricesMinor?: Record<string, number>;
}

/** Explicit-only mock: callers must opt in; production never silently uses it. */
export function createMockLightspeedClient(
  options: MockLightspeedOptions = {},
): LightspeedClient {
  const account: PosAccountSnapshot = {
    accountId: options.account?.accountId ?? "dev-account",
    totalAmountMinor: options.account?.totalAmountMinor ?? 0,
    paidAmountMinor: options.account?.paidAmountMinor ?? 0,
    currency: options.account?.currency ?? "EUR",
    lines: options.account?.lines ? [...options.account.lines] : [],
    payments: options.account?.payments ? [...options.account.payments] : [],
  };
  const data = new Map<string, unknown>();
  const snapshot = () => ({
    ...account,
    lines: [...account.lines],
    payments: [...account.payments],
  });
  return {
    getCurrentAccount: async () => snapshot(),
    load: async <T>(key: string) => data.get(key) as T | undefined,
    store: async (key, value) => {
      data.set(key, value);
    },
    addItem: async (itemId, quantity = 1) => {
      const amount = (options.itemPricesMinor?.[itemId] ?? 0) * quantity;
      account.lines.push({ itemId, quantity, totalAmountMinor: amount });
      account.totalAmountMinor += amount;
    },
    addPayment: async ({ amountMinor, paymentMethodId }) => {
      account.payments.push({ amountMinor, reference: paymentMethodId });
      account.paidAmountMinor += amountMinor;
      return snapshot();
    },
    addExternalReference: async () => undefined,
    payAccount: async () => snapshot(),
    commitAccount: async () => snapshot(),
    navigate: async () => undefined,
    close: async () => undefined,
  };
}

export function isDevelopmentBridgeFallbackAllowed(): boolean {
  const env = (
    globalThis as { process?: { env?: Record<string, string | undefined> } }
  ).process?.env?.NODE_ENV;
  const host = (globalThis as { location?: { hostname?: string } }).location
    ?.hostname;
  return (
    env === "development" ||
    env === "test" ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]"
  );
}

export function getLightspeedClientForBrowser(
  options: MockLightspeedOptions = {},
): LightspeedClient {
  if (typeof (globalThis as Bridge).pos_getCurrentAccount === "function")
    return createLightspeedClient();
  if (isDevelopmentBridgeFallbackAllowed())
    return createMockLightspeedClient(options);
  throw new Error(
    "Lightspeed POS bridge not found; refusing to use a mock outside development",
  );
}
